import { useContext, useEffect, useRef, useState, useMemo } from "react";
import { WorkspaceContext } from "src/context/workspace";
import { classNames } from "src/helpers/classNames";
import { addClickPrevention, addSecurityTags, fixIframeUrls, fixInvalidLinks, prepareEmbeddedHtml } from "src/helpers/pageHelper";
import useDevice from "src/hooks/useDevice";

export default function CustomHtmlEmbed({ content }) {
  const { details: workspaceDetails } = useContext(WorkspaceContext);
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [iframeHeight, setIframeHeight] = useState("500px");
  const iframeRef = useRef(null);
  const device = useDevice();

  const finalHtml = useMemo(() => {
    try {
      let rawHtml = content?.custom_html || "";

      if (!rawHtml) {
        return "";
      }

      const viewportMeta = `<meta name="viewport" content="width=device-width, initial-scale=1.0">`;
      const iframeStyles = `<style>
        html, body {
          min-height: 100%;
          margin: 0;
          padding: 0;
          overflow-x: auto !important;
          overflow-y: auto !important;
          -webkit-overflow-scrolling: touch;
          box-sizing: border-box;
        }
        body {
          width: 100%;
          max-width: 100%;
        }
        img, video, iframe, object, embed {
          max-width: 100% !important;
        }
        table {
          max-width: 100%;
          overflow-x: auto;
          display: block;
        }
        * {
          box-sizing: border-box;
        }
      </style>`;

      let prepared = prepareEmbeddedHtml(rawHtml);

      const injectedHeadContent = `${viewportMeta}\n${iframeStyles}`;

      if (/(<\/head>)/i.test(prepared)) {
        prepared = prepared.replace(/(<\/head>)/i, () => `${injectedHeadContent}</head>`);
      } else if (/(<head[^>]*>)/i.test(prepared)) {
        prepared = prepared.replace(/(<head[^>]*>)/i, (match) => `${match}${injectedHeadContent}`);
      } else if (/(<html[^>]*>)/i.test(prepared)) {
        prepared = prepared.replace(/(<html[^>]*>)/i, (match) => `${match}<head>${injectedHeadContent}</head>`);
      } else {
        prepared = `<head>${injectedHeadContent}</head>${prepared}`;
      }

      prepared = addSecurityTags(prepared);
      prepared = fixInvalidLinks(prepared);
      prepared = fixIframeUrls(prepared);
      prepared = addClickPrevention(prepared);

      return prepared;
    } catch (error) {
      console.error("Error processing custom HTML:", error);
      return "";
    }
  }, [content]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || !finalHtml) return;

    let resizeObserver = null;
    let timer1 = null;
    let timer2 = null;

    const updateHeight = () => {
      try {
        const doc = iframe.contentDocument || iframe.contentWindow?.document;
        if (doc) {
          const bodyHeight = doc.body?.scrollHeight || 0;
          const htmlHeight = doc.documentElement?.scrollHeight || 0;
          const calculatedHeight = Math.max(bodyHeight, htmlHeight);
          if (calculatedHeight > 0) {
            setIframeHeight(`${calculatedHeight}px`);
          }
        }
      } catch (e) {
        // Fallback for cross-origin or restricted iframe access
        console.log(e, "error");
      }
    };

    try {
      const doc = iframe.contentDocument || iframe.contentWindow?.document;
      if (doc) {
        doc.open();
        doc.write(finalHtml);
        doc.close();

        setIsIframeLoaded(true);
        updateHeight();
        timer1 = setTimeout(updateHeight, 150);
        timer2 = setTimeout(updateHeight, 600);

        if (doc.body && window.ResizeObserver) {
          resizeObserver = new ResizeObserver(updateHeight);
          resizeObserver.observe(doc.body);
        }
      }
    } catch (e) {
      console.warn("Iframe write failed:", e);
    }

    return () => {
      if (resizeObserver) resizeObserver.disconnect();
      if (timer1) clearTimeout(timer1);
      if (timer2) clearTimeout(timer2);
    };
  }, [finalHtml]);

  return (
    <div className={classNames("relative h-full min-h-[400px] w-full flex-1 overflow-auto")}>
      <div
        className={"relative z-0 mx-auto h-full min-h-[400px] w-full overflow-auto"}
        style={{ maxWidth: device === "desktop" ? workspaceDetails?.max_width || "100%" : "100%" }}>
        <iframe
          ref={iframeRef}
          id="iframe"
          title="Custom Html Embed"
          width="100%"
          height={iframeHeight || "100%"}
          sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals"
          style={{
            visibility: isIframeLoaded ? "visible" : "hidden",
            border: "none",
            margin: "0",
            width: "100%",
            height: iframeHeight || "100%",
            minHeight: "400px",
            display: "block",
          }}
        />
      </div>
    </div>
  );
}
